import React from 'react'
import { cn } from '../lib/utils'

interface ErrorBoundaryProps {
  children: React.ReactNode
  fallback?: React.ReactNode
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

/**
 * Enterprise-grade Error Boundary (Brayhard Hardened).
 * Intercepts uncaught rendering exceptions and displays a human-friendly,
 * action-oriented recovery UI without leaking raw stack traces to production users.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.props.onError?.(error, errorInfo)
    console.error('[Brayhard ErrorBoundary Exception]:', error, errorInfo)
  }

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="flex min-h-[500px] w-full items-center justify-center p-6 bg-slate-50/50 dark:bg-slate-900/50">
          <div className="w-full max-w-lg rounded-2xl border border-slate-200/80 bg-white p-8 text-center shadow-xl shadow-slate-200/50 dark:border-slate-800 dark:bg-slate-900 dark:shadow-none">
            <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-amber-100/80 ring-8 ring-amber-50 dark:bg-amber-900/30 dark:ring-amber-950/50">
              <svg
                className="h-10 w-10 text-amber-600 dark:text-amber-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z m9-4v4m0 4h.01"
                />
              </svg>
            </div>

            <h3 className="mb-2 text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
              Display Interruption
            </h3>

            <p className="mb-6 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
              We hit a minor display hiccup, but your offline data and pending transactions remain safely stored on this device.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => {
                  this.setState({ hasError: false, error: null })
                }}
                className="w-full sm:w-auto rounded-xl bg-green-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-green-700 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              >
                Retry View
              </button>

              <button
                onClick={() => {
                  this.setState({ hasError: false, error: null })
                  window.location.reload()
                }}
                className="w-full sm:w-auto rounded-xl border border-slate-300 bg-white px-6 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 transition-colors dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                Reload Application
              </button>
            </div>

            {(typeof window !== 'undefined' && (window as any).__DEV__ === true) && this.state.error && (
              <div className="mt-6 border-t border-slate-100 pt-4 text-left dark:border-slate-800">
                <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Developer Stack Trace (Hidden in Prod)
                </p>
                <pre className="max-h-40 overflow-auto rounded-lg bg-slate-100 p-3 font-mono text-xs text-slate-700 dark:bg-slate-950 dark:text-slate-300">
                  {this.state.error.stack ?? this.state.error.message}
                </pre>
              </div>
            )}
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
